import { useRef } from 'react';
import Editor, { type OnMount } from '@monaco-editor/react';
import { App, Button, Popconfirm, Tooltip } from 'antd';
import {
  ClearOutlined,
  CopyOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  FormatPainterOutlined,
} from '@ant-design/icons';
import type { editor } from 'monaco-editor';

interface ScriptEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  themeMode: 'light' | 'dark';
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  /** Hidden (not unmounted) while a sibling script editor is fullscreen, so Monaco's model survives the toggle. */
  hidden?: boolean;
}

/**
 * A pre-request/test script editor with a small real toolbar on top of
 * Monaco — Format/Search/Replace/Undo/Redo/Go-to-line are Monaco's own
 * built-in commands (Shift+Alt+F, Ctrl+F, Ctrl+H, Ctrl+Z, Ctrl+G all work
 * out of the box), so this only adds what Monaco doesn't already give for
 * free: Copy, Clear, and a fullscreen toggle.
 */
export const ScriptEditor = ({
  label,
  value,
  onChange,
  themeMode,
  isFullscreen,
  onToggleFullscreen,
  hidden = false,
}: ScriptEditorProps) => {
  const { message } = App.useApp();
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const handleMount: OnMount = (editorInstance) => {
    editorRef.current = editorInstance;
  };

  const handleFormat = () => {
    editorRef.current?.getAction('editor.action.formatDocument')?.run();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      void message.success('Copied to clipboard');
    } catch {
      void message.error('Failed to copy — clipboard access was denied');
    }
  };

  return (
    <div
      // Hidden via CSS rather than unmounted — keeps the Monaco model (and
      // its undo/redo history and any open find/replace state) alive across
      // a sibling's fullscreen toggle instead of losing it every time.
      style={hidden ? { display: 'none' } : undefined}
      className={`flex min-h-0 flex-col ${isFullscreen ? 'h-full flex-1' : 'flex-1'}`}
    >
      <div className="mb-1 flex items-center justify-between">
        <label className="block text-xs text-gray-500">{label}</label>
        <div className="flex items-center gap-0.5">
          <Tooltip title="Format document (Shift+Alt+F)">
            <Button
              size="small"
              type="text"
              icon={<FormatPainterOutlined />}
              aria-label={`Format ${label}`}
              onClick={handleFormat}
            />
          </Tooltip>
          <Tooltip title="Copy script">
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              aria-label={`Copy ${label}`}
              onClick={() => void handleCopy()}
            />
          </Tooltip>
          <Popconfirm title="Clear this script?" onConfirm={() => onChange('')} disabled={!value}>
            <Button
              size="small"
              type="text"
              danger
              icon={<ClearOutlined />}
              aria-label={`Clear ${label}`}
              title="Clear script"
              disabled={!value}
            />
          </Popconfirm>
          <Tooltip title={isFullscreen ? 'Restore' : 'Fullscreen'}>
            <Button
              size="small"
              type="text"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              aria-label={isFullscreen ? `Restore ${label}` : `Fullscreen ${label}`}
              onClick={onToggleFullscreen}
            />
          </Tooltip>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden rounded border border-black/10 dark:border-white/10">
        <Editor
          height="100%"
          language="javascript"
          theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
          value={value}
          onChange={(next) => onChange(next ?? '')}
          onMount={handleMount}
          options={{
            minimap: { enabled: false },
            fontSize: 12,
            wordWrap: 'on',
            scrollBeyondLastLine: false,
          }}
        />
      </div>
    </div>
  );
};
